import { useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertCircle, HardHat, Loader2 } from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import { useAuth } from "@/features/auth/auth-provider";
import { resolveGetStartedDestination } from "@/features/marketing/resolve-get-started-destination";
import { Button } from "@/components/ui/button";
import { InfoBanner } from "@/components/design/info-banner";

/** Deep-link entry for marketing Get Started — login then route by allowlist. */
export function GetStartedCtaPage() {
  const {
    ready,
    authenticated,
    serverAuthenticated,
    redirectTo,
    sessionError,
    login,
  } = useAuth();
  const loginTriggered = useRef(false);

  useEffect(() => {
    document.title = `Get started — ${APP_NAME}`;
  }, []);

  // Open Privy once when the page loads unauthenticated — never re-open in a loop.
  useEffect(() => {
    if (!ready || authenticated || loginTriggered.current) return;
    loginTriggered.current = true;
    login();
  }, [ready, authenticated, login]);

  if (ready && authenticated && serverAuthenticated && redirectTo) {
    return <Navigate to={resolveGetStartedDestination(redirectTo)} replace />;
  }

  const checkingSession = authenticated && !ready;
  const sessionFailed =
    ready && authenticated && (!serverAuthenticated || Boolean(sessionError));

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted/30 p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center text-sm text-muted-foreground">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <HardHat className="h-6 w-6" />
        </div>

        {!ready || checkingSession ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
            <p>Getting started…</p>
          </>
        ) : null}

        {sessionFailed ? (
          <div className="w-full space-y-3 text-left">
            <InfoBanner icon={AlertCircle} title="Sign-in could not be completed" variant="error">
              {sessionError ?? "The server could not verify your session. Please try again."}
            </InfoBanner>
            <Button className="w-full" onClick={login}>
              Try again
            </Button>
          </div>
        ) : null}

        {ready && !authenticated ? (
          <div className="w-full space-y-3">
            <p>
              Sign in with your allowlisted work email to continue to your workspace or create one.
            </p>
            <Button
              className="w-full"
              onClick={() => {
                loginTriggered.current = true;
                login();
              }}
            >
              Continue with email
            </Button>
          </div>
        ) : null}

        <p>
          <Link to="/demo" className="font-medium text-orange-construction hover:underline">
            Try the demo
          </Link>
          {" · "}
          <Link to="/" className="font-medium text-navy-deep hover:underline">
            Home
          </Link>
        </p>
      </div>
    </div>
  );
}
